import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import { Invoice, ShippedItem } from '../types';
import openSans from '../../../../fonts/OpenSans-Regular.ttf';
import openSansBold from '../../../../fonts/OpenSans-Bold.ttf';
import { formatStandardDate } from '../../waterline/utils';

Font.register({ family: 'Open Sans', fonts: [{ src: openSans }, { src: openSansBold, fontWeight: 'bold' }] });

const DEFAULT_TEXT_COLOR = '#50555b';

const styles = StyleSheet.create({
  page: {
    color: DEFAULT_TEXT_COLOR,
    padding: '16px',
    fontFamily: 'Open Sans'
  },
  header: {
    borderBottom: `1 solid ${DEFAULT_TEXT_COLOR}`,
    paddingBottom: 8,
    marginBottom: 10
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold'
  },
  body: {
    fontSize: 10,
    marginBottom: 10
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  section: {
    flex: 1,
    paddingRight: 10
  },
  table: {
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 10
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row'
  },
  tableColHeader: {
    width: '9%',
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderRightWidth: 1,
    padding: 2,
    fontWeight: 'bold',
    fontSize: 8
  },
  tableCol: {
    width: '9%',
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderRightWidth: 1,
    padding: 2
  },
  tableCell: {
    margin: 'auto',
    marginTop: 5,
    fontSize: 8
  },
  footerText: {
    marginTop: 10,
    fontStyle: 'italic'
  }
});

const TableRow: React.FC<{ item: ShippedItem }> = ({ item }) => (
  <View style={styles.tableRow}>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.Ndc}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.ItemNumber}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.Description}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.LotNumber}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{formatStandardDate(item.ExpirationDate)}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.Strength}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.DosageForm}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.NetContentDescription}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}> {item.UnitOfMeasure}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}> {item.Quantity}</Text>
    </Text>
    <Text style={styles.tableCol}>
      <Text style={styles.tableCell}>{item.SerialNumber}</Text>
    </Text>
  </View>
);

const InvoicePDF: React.FC<{ invoice: Invoice }> = ({ invoice }) => {
  let buyerName = '';
  let buyerAddress = '';

  if (invoice?.BuyerAddress) {
    const { Name, Address1, City, State, ZipCode } = invoice.BuyerAddress;

    buyerName = Name;
    buyerAddress = `${Address1} ${City} ${State} ${ZipCode}`;
  }

  console.log('inoice ', invoice)

  return (
    <Document>
      <Page size="A4" orientation="landscape" wrap style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Track and Trace Report</Text>
        </View>

        <View style={styles.body}>
          <View style={styles.row}>
            <View style={styles.section}>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Transaction Date:</Text> {formatStandardDate(invoice.TransactionDate)}
              </Text>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Shipment Date:</Text> {formatStandardDate(invoice.ShipmentDate)}
              </Text>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Invoice Number:</Text> {invoice.InvoiceNumber}
              </Text>
            </View>
            <View style={styles.section}>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Seller Name:</Text> {invoice.SellerAddress.Name}
              </Text>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Seller Address:</Text> {invoice.SellerAddress.Address1}, {invoice.SellerAddress.City},{' '}
                {invoice.SellerAddress.State} {invoice.SellerAddress.ZipCode}
              </Text>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Buyer Name:</Text> {buyerName}
              </Text>
              <Text>
                <Text style={{ fontWeight: 'bold' }}>Buyer Address:</Text> {buyerAddress}
              </Text>
            </View>
          </View>

          <Text style={{ marginVertical: 10, fontWeight: 'bold' }}>Product Details:</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              {[
                'NDC',
                'Item Number',
                'Description',
                'Lot Number',
                'Expiration Date',
                'Strength',
                'Dosage Form',
                'Size',
                'Unit of Measure',
                'Quantity',
                'Serial'
              ].map((header, idx) => (
                <Text key={idx} style={styles.tableColHeader}>
                  {header}
                </Text>
              ))}
            </View>

            {invoice.ShippedItems.length === 0 ? (
              <View style={styles.tableRow}>
                <Text style={{ padding: 5, fontStyle: 'italic', flex: 1 }}>There are no products to display</Text>
              </View>
            ) : (
              invoice.ShippedItems.map((item, index) => <TableRow item={item} key={index} />)
            )}
          </View>

          <Text style={styles.footerText}>Transaction Statement:</Text>
          <Text>Seller has complied with each applicable subsection of FDCA Sec. 581(27)(A)-(G)</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
